import { useState, useEffect, useRef } from "react";
import { Box, IconButton, Modal } from "@mui/material";
import { ChevronLeft, ChevronRight, Close } from "@mui/icons-material";

type NavigationType = "dots" | "arrows" | "both" | "disabled";

interface Props {
  images: string[];
  alt?: string;
  height?: number;
  navigation?: NavigationType;
  autoplay?: boolean;
  autoplayInterval?: number;
}

export const Carousel = ({ images, alt = "Image", height = 200, navigation = "both", autoplay = true, autoplayInterval = 5000 }: Props) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const autoplayTimerRef = useRef<NodeJS.Timeout | null>(null);

  const resetAutoplay = () => {
    if (autoplayTimerRef.current) {
      clearInterval(autoplayTimerRef.current);
    }

    if (!autoplay || images.length <= 1) return;

    autoplayTimerRef.current = setInterval(() => {
      setDirection("right");
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, autoplayInterval);
  };

  useEffect(() => {
    resetAutoplay();
    return () => {
      if (autoplayTimerRef.current) {
        clearInterval(autoplayTimerRef.current);
      }
    };
  }, [autoplay, autoplayInterval, images.length]);

  const goNext = () => {
    setDirection("right");
    setCurrentIndex((prev) => (prev + 1) % images.length);
    resetAutoplay();
  };

  const goPrev = () => {
    setDirection("left");
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    resetAutoplay();
  };

  const goLightboxNext = () => {
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const goLightboxPrev = () => {
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleLightboxKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowRight") goLightboxNext();
    if (e.key === "ArrowLeft") goLightboxPrev();
    if (e.key === "Escape") setLightboxOpen(false);
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setDragStart(e.clientX);
  };

  const handleMouseUp = (e: React.MouseEvent) => {
    if (dragStart === null) return;

    const diff = dragStart - e.clientX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }

    setDragStart(null);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setDragStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (dragStart === null) return;

    const diff = dragStart - e.changedTouches[0].clientX;

    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        goNext();
      } else {
        goPrev();
      }
    }

    setDragStart(null);
  };

  const handleImageClick = () => {
    setLightboxIndex(currentIndex);
    setLightboxOpen(true);
  };

  const showArrows = navigation === "arrows" || navigation === "both";
  const showDots = navigation === "dots" || navigation === "both";

  return (
    <>
      <Box
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={() => setDragStart(null)}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleImageClick}
        sx={{
          position: "relative",
          width: "100%",
          height: { xs: 220, md: `${height}px` },
          overflow: "hidden",
          borderRadius: 2,
          backgroundColor: "var(--fx-surface-muted, #F1F5F9)",
          cursor: dragStart !== null ? "grabbing" : "zoom-in",
          userSelect: "none",
        }}
      >
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          {images.map((img, idx) => (
            <Box
              key={idx}
              component="img"
              src={img}
              alt={`${alt} ${idx}`}
              sx={{
                position: "absolute",
                width: "100%",
                height: "100%",
                objectFit: { xs: "contain", md: "cover" },
                opacity: idx === currentIndex ? 1 : 0,
                transition: "opacity 0.6s ease-in-out, transform 0.6s ease-in-out",
                transform: idx === currentIndex ? "translateX(0)" : direction === "right" ? "translateX(100%)" : "translateX(-100%)",
              }}
            />
          ))}
        </Box>

        {showArrows && images.length > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goPrev();
            }}
            sx={{
              position: "absolute",
              left: 16,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 1)",
              },
            }}
          >
            <ChevronLeft />
          </IconButton>
        )}

        {showArrows && images.length > 1 && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              goNext();
            }}
            sx={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.8)",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 1)",
              },
            }}
          >
            <ChevronRight />
          </IconButton>
        )}

        {showDots && images.length > 1 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 16,
              left: "50%",
              transform: "translateX(-50%)",
              display: "flex",
              gap: 1,
              zIndex: 10,
            }}
          >
            {images.map((_, idx) => (
              <Box
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (idx > currentIndex) setDirection("right");
                  if (idx < currentIndex) setDirection("left");
                  setCurrentIndex(idx);
                  resetAutoplay();
                }}
                sx={{
                  width: idx === currentIndex ? 24 : 8,
                  height: 8,
                  borderRadius: 1,
                  backgroundColor: idx === currentIndex ? "var(--fx-surface-base, #FFFFFF)" : "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.6)",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      <Modal
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        slotProps={{
          backdrop: {
            sx: {
              zIndex: 99999,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(0.5px)",
              transition: "all 0.3s ease",
            },
          },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 999999,
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "90vw",
            height: "90vh",
            maxWidth: "1200px",
            maxHeight: "800px",
            backgroundColor: "transparent",
            borderRadius: 2,
            zIndex: 999999,
            animation: "fadeInScale 0.3s ease-out",
            "@keyframes fadeInScale": {
              from: {
                opacity: 0,
                transform: "scale(0.95)",
              },
              to: {
                opacity: 1,
                transform: "scale(1)",
              },
            },
          }}
          onClick={() => setLightboxOpen(false)}
          onKeyDown={handleLightboxKeyDown}
          tabIndex={0}
          autoFocus
        >
          <Box
            component="img"
            src={images[lightboxIndex]}
            alt={`${alt} ${lightboxIndex}`}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              animation: "fadeIn 0.4s ease-out",
              "@keyframes fadeIn": {
                from: { opacity: 0 },
                to: { opacity: 1 },
              },
            }}
            onClick={(e) => e.stopPropagation()}
          />

          <IconButton
            onClick={() => setLightboxOpen(false)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.2)",
              color: "#fff",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.3)",
              },
            }}
          >
            <Close />
          </IconButton>

          {images.length > 1 && (
            <>
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  goLightboxPrev();
                }}
                sx={{
                  position: "absolute",
                  left: -64,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.2)",
                  color: "#fff",
                  zIndex: 10,
                  "&:hover": {
                    backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.3)",
                  },
                }}
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  goLightboxNext();
                }}
                sx={{
                  position: "absolute",
                  right: -64,
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.2)",
                  color: "#fff",
                  zIndex: 10,
                  "&:hover": {
                    backgroundColor: "rgba(var(--fx-surface-basergb, 255, 255, 255), 0.3)",
                  },
                }}
              >
                <ChevronRight />
              </IconButton>

              <Box
                sx={{
                  position: "absolute",
                  bottom: 16,
                  left: "50%",
                  transform: "translateX(-50%)",
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "#fff",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  fontSize: "0.9rem",
                  zIndex: 10,
                }}
              >
                {lightboxIndex + 1} / {images.length}
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};
